import { Component, ojs, app, payload, v } from "modular-openscriptjs";
import { uc } from "../../ojs.contexts";
import { dom, tool } from "../../fotastart";
import { cleanDispose, empty, eParse, inform, isMobile, tab } from "../../helpers";
import { STRINGS } from "../../declarations";

const h = app("h");
const broker = app("broker");
const $e = app("$e");

export default class AddProductForm extends Component {
    holder = null;
    currentTab = "text";

    render(...args) {
        return h.empty();
    }

    async showForm(ed, event) {
        if (this.holder) {
            cleanDispose(this.holder);
        }

        let {message} = eParse(ed);

        let initialText = null;

        if(!empty(message.get('initialText', null))) {
            initialText = message.get('initialText');
        }

        this.currentTab = "text";

        if (isMobile()) {
            this.holder = tool.offcanvas(
                [
                    h.AddProductCanvas(this.renderForm(initialText)),
                    h.div(
                        {
                            class: "offcanvas-footer p-3",
                            style: "flex-shrink: 0;",
                        },
                        this.renderFooter(),
                    ),
                ],
                { backdrop: true },
            );
        } else {
            this.holder = tool.plainModal(
                h.AddProductModal(this.renderForm(), this.renderFooter()),
                { backdrop: true },
            );
        }

        this.holder.show();
    }

    closeForm() {
        if (this.holder) {
            this.holder.hide();
        }
    }

    selectTab(name) {
        this.currentTab = name;
    }

    triggerImagePicker() {
        dom.id("add-product-form-images-input")?.click();
    }

    async handleImagesSelected(e) {
        const files = e.target.files;
        const current = uc.pendingShoppingImages.value || [];
        const incoming = files ? Array.from(files).filter((f) => f.type.startsWith("image/")) : [];

        e.target.value = "";

        if (incoming.length === 0) return;

        const newImages = [];

        for (const file of incoming) {
            // Force the file to read its binary array buffer immediately into memory
            const arrayBuffer = await file.arrayBuffer();

            // Re-instantiate it as a clean, self-contained Web Blob so it survives
            // even if the original input/file reference gets recycled by the browser.
            const robustBlob = new Blob([arrayBuffer], { type: file.type });

            newImages.push({
                file: robustBlob,
                name: file.name,
                url: URL.createObjectURL(robustBlob),
            });
        }

        uc.pendingShoppingImages.value = [...current, ...newImages];
    }

    removeImage(idx) {
        const current = [...(uc.pendingShoppingImages.value || [])];
        if (current[idx]?.url) URL.revokeObjectURL(current[idx].url);
        current.splice(idx, 1);
        uc.pendingShoppingImages.value = current;
    }

    saveItems() {
        if (this.currentTab === "picture") {
            const images = uc.pendingShoppingImages.value || [];

            if (images.length === 0) {
                return inform("Please add at least one picture", STRINGS.INFO);
            }

            this.closeForm();
            broker.send($e.userProduct.needs.progressForm, payload({ mode: "images" }));
            return;
        }

        const textarea = dom.id("add-product-form-input");
        const prompt = textarea?.value?.trim();

        if (!prompt) return inform("Please type what you want", STRINGS.INFO);

        uc.pendingShoppingList.value = prompt;

        this.closeForm();
        broker.send($e.userProduct.needs.progressForm, payload({ prompt }));
    }

    renderForm(initialText) {
        const styleBlock = h.style(`
            .aif-tab-btn {
                color: #64748b;
                background: transparent;
            }
            .aif-tab-btn.active {
                background: linear-gradient(135deg, #6366f1 0%, #a855f7 50%, #ec4899 100%);
                color: #fff;
                box-shadow: 0 2px 8px rgba(99, 102, 241, 0.25);
            }
        `);

        return h.div(
            { class: "w-100 text-start" },
            styleBlock,

            // Header
            h.div(
                {
                    class: "d-flex justify-content-between align-items-center mb-4",
                },
                h.div(
                    { class: "d-flex align-items-center gap-3" },
                    h.div(
                        {
                            class: "d-flex align-items-center justify-content-center rounded-circle flex-shrink-0",
                            style: [
                                "width: 40px; height: 40px;",
                                "background: linear-gradient(135deg, #6366f1 0%, #a855f7 50%, #ec4899 100%);",
                            ].join(" "),
                        },
                        h.i({
                            class: "bi bi-stars text-white",
                            style: "font-size: 15px;",
                        }),
                    ),
                    h.div(
                        h.p(
                            {
                                class: "fw-bold mb-0",
                                style: "font-size: 15px;",
                            },
                            "Add Items",
                        ),
                        h.p(
                            { class: "text-muted mb-0 small" },
                            "AI will extract each item for you",
                        ),
                    ),
                ),
                h.button({
                    class: "btn-close bg-light rounded-circle p-2",
                    onclick: this.method("closeForm"),
                    aria_label: "Close",
                }),
            ),

            // Mode tabs
            h.Tabs({
                holders: {
                    nav: h.div({
                        class: "d-flex gap-1 p-1 rounded-pill mb-3",
                        style: "background: var(--bs-tertiary-bg, #f1f5f9);",
                    }),
                    body: h.div({ class: "tab-content" }),
                },
                tabs: [
                    tab(
                        h.button(
                            {
                                class: "aif-tab-btn nav-link active flex-fill rounded-pill border-0 fw-bold py-2 d-flex align-items-center justify-content-center gap-2",
                                listeners: {
                                    click: () => this.selectTab("text"),
                                },
                            },
                            h.i({ class: "bi bi-type" }),
                            h.span("Text"),
                        ),
                        h.div(
                            { class: "tab-pane fade show active" },
                            this.renderTextPane(initialText),
                        ),
                    ),
                    tab(
                        h.button(
                            {
                                class: "aif-tab-btn nav-link flex-fill rounded-pill border-0 fw-bold py-2 d-flex align-items-center justify-content-center gap-2",
                                listeners: {
                                    click: () => this.selectTab("picture"),
                                },
                            },
                            h.i({ class: "bi bi-camera-fill" }),
                            h.span("Picture"),
                        ),
                        h.div(
                            { class: "tab-pane fade" },
                            this.renderPicturePane(),
                        ),
                    ),
                ],
            }),
        );
    }

    renderTextPane(initialText) {
        return h.div(
            { class: "text-start" },

            // Textarea
            h.div(
                { class: "position-relative mb-2" },
                v(uc.pendingShoppingList, (list) =>
                    h.textarea(
                        {
                            id: "add-product-form-input",
                            class: "form-control border-0 rounded-4 p-3",
                            style: [
                                "min-height: 160px;",
                                "resize: none;",
                                "background: var(--bs-tertiary-bg, #f8f9fa);",
                                "font-size: 15px;",
                                "line-height: 1.6;",
                            ].join(" "),
                            placeholder: [
                                "e.g. Fries from ABC, 1 green pepper,",
                                "1 steak of beef and 2 litres of milk...",
                            ].join("\n"),
                            autofocus: true,
                        },
                        initialText || list.value || "",
                    ),
                ),
                // Floating AI badge — bottom-right inside textarea
                h.div(
                    {
                        class: "position-absolute d-flex align-items-center gap-1",
                        style: "bottom: 12px; right: 14px; pointer-events: none;",
                    },
                    h.i({
                        class: "bi bi-stars",
                        style: [
                            "font-size: 10px;",
                            "background: linear-gradient(135deg, #6366f1, #ec4899);",
                            "-webkit-background-clip: text;",
                            "-webkit-text-fill-color: transparent;",
                        ].join(" "),
                    }),
                    h.span(
                        {
                            class: "fw-semibold",
                            style: [
                                "font-size: 10px;",
                                "letter-spacing: 0.3px;",
                                "background: linear-gradient(135deg, #6366f1, #ec4899);",
                                "-webkit-background-clip: text;",
                                "-webkit-text-fill-color: transparent;",
                            ].join(" "),
                        },
                        "AI Powered",
                    ),
                ),
            ),

            // Helper text
            h.p(
                {
                    class: "text-muted small px-1 mb-0 d-flex gap-2 mt-2 align-items-center",
                },
                h.i({ class: "bi bi-info-circle" }),
                h.span("Type what you need"),
            ),
            h.p(
                { class: "text-muted small px-1 mt-1 mb-0" },
                h.i({ class: "bi bi-stars me-1" }),
                "We'll use your AI credit.",
            ),
        );
    }

    renderPicturePane() {
        return h.div(
            { class: "text-start" },

            h.div(
                {
                    class: "rounded-4 p-4 text-center mb-3",
                    style: [
                        "border: 2px dashed #c7d2fe;",
                        "background: var(--bs-tertiary-bg, #f8f9fa);",
                        "cursor: pointer;",
                    ].join(" "),
                    listeners: {
                        click: () => this.triggerImagePicker(),
                    },
                },
                h.i({
                    class: "bi bi-camera-fill d-block mb-2",
                    style: [
                        "font-size: 28px;",
                        "background: linear-gradient(135deg, #6366f1, #ec4899);",
                        "-webkit-background-clip: text;",
                        "-webkit-text-fill-color: transparent;",
                    ].join(" "),
                }),
                h.p(
                    { class: "fw-bold mb-1", style: "font-size: 14px;" },
                    "Take or Choose Pictures",
                ),
                h.p(
                    { class: "text-muted small mb-0" },
                    "Snap your items, a receipt, or a shelf. Add as many as you need.",
                ),
            ),

            h.input({
                type: "file",
                accept: "image/*",
                multiple: "multiple",
                capture: "environment",
                class: "d-none",
                id: "add-product-form-images-input",
                listeners: {
                    change: (e) => this.handleImagesSelected(e),
                },
            }),

            v(uc.pendingShoppingImages, (imgs) => {
                if (!imgs.value || imgs.value.length === 0) return h.empty();

                return h.div(
                    { class: "mb-2" },
                    h.p(
                        { class: "text-muted small fw-semibold mb-2" },
                        `${imgs.value.length} picture${imgs.value.length > 1 ? "s" : ""} added`,
                    ),
                    h.div(
                        { class: "row g-2" },
                        ...imgs.value.map((img, idx) =>
                            h.div(
                                { class: "col-4" },
                                h.div(
                                    {
                                        class: "position-relative border rounded-3 overflow-hidden bg-light",
                                        style: "padding-top: 100%;",
                                    },
                                    h.img({
                                        src: img.url,
                                        class: "position-absolute top-0 start-0 w-100 h-100",
                                        style: "object-fit: cover;",
                                    }),
                                    h.button({
                                        class: "btn btn-danger btn-sm rounded-circle position-absolute",
                                        style: [
                                            "top: 4px; right: 4px;",
                                            "width: 22px; height: 22px; padding: 0;",
                                            "display: flex; align-items: center; justify-content: center;",
                                        ].join(" "),
                                        listeners: {
                                            click: () => this.removeImage(idx),
                                        },
                                    },
                                        h.i({
                                            class: "bi bi-x-lg",
                                            style: "font-size: 10px;",
                                        }),
                                    ),
                                ),
                            ),
                        ),
                    ),
                );
            }),

            h.p(
                {
                    class: "text-muted small px-1 mb-0 d-flex gap-2 mt-2 align-items-center",
                },
                h.i({ class: "bi bi-info-circle" }),
                h.span("Take clear pictures of your items"),
            ),
            h.p(
                { class: "text-muted small px-1 mt-1 mb-0" },
                h.i({ class: "bi bi-stars me-1" }),
                "We'll use your AI credit.",
            ),
        );
    }

    renderFooter() {
        return h.div(
            {
                class: "d-flex justify-content-between align-items-center gap-3 w-100",
            },
            h.button(
                {
                    class: "btn btn-light rounded-pill px-4 py-3 fw-bold flex-fill",
                    onclick: this.method("closeForm"),
                },
                "Cancel",
            ),
            h.button(
                {
                    id: "add-product-form-save-btn",
                    class: [
                        "btn rounded-pill px-4 py-3 fw-bold flex-fill",
                        "d-flex align-items-center justify-content-center gap-2",
                        "text-white border-0",
                    ].join(" "),
                    style: "background: linear-gradient(135deg, #6366f1 0%, #a855f7 50%, #ec4899 100%);",
                    onclick: this.method("saveItems"),
                },
                h.i({ class: "bi bi-stars" }),
                h.span("Save Items"),
            ),
        );
    }

    $$product = {
        needs: {
            productForm: (ed, event) => this.showForm(ed, event),
        },
    };

    cleanUp() {
        super.cleanUp();
        if (this.holder) cleanDispose(this.holder);
    }
}

export function AddProductCanvas(bodyContent) {
    return h.$(
        h.div(
            {
                class: "offcanvas-body p-4",
                style: "flex-grow: 1; overflow-y: auto;",
            },
            bodyContent,
        ),
    );
}

export function AddProductModal(bodyContent, footerContent) {
    return h.$(
        h.div(
            {
                class: "modal-content border-0 rounded-4 shadow-lg",
            },
            h.div(
                { class: "modal-body p-4" },
                bodyContent,
                h.div(
                    { class: "modal-footer border-0 p-0 pt-3" },
                    footerContent,
                ),
            ),
        ),
    );
}

ojs(AddProductForm, AddProductCanvas, AddProductModal);
